import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Edit, Security } from '@mui/icons-material';
import apiService from '../../services/api';
import { PermissionLookup, RoleDetails } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

type StatusMessage = { type: 'success' | 'error'; text: string };

const RoleManagementPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const canViewRoleManagement = hasPermission('admin.role_management.view');
  const canManageRoleManagement = hasPermission('admin.role_management.manage');

  const [roles, setRoles] = useState<RoleDetails[]>([]);
  const [permissions, setPermissions] = useState<PermissionLookup[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [roleName, setRoleName] = useState('');
  const [rolePermissions, setRolePermissions] = useState<string[]>([]);
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null);
  const [roleSaving, setRoleSaving] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(true);

  const loadRoles = useCallback(async () => {
    if (!canViewRoleManagement) {
      setRoles([]);
      setLoadingRoles(false);
      return;
    }

    setLoadingRoles(true);
    try {
      const data = await apiService.getRoles();
      setRoles(data);
    } catch (error) {
      console.error('Failed to load roles:', error);
      setStatusMessage({ type: 'error', text: 'Unable to load roles' });
    } finally {
      setLoadingRoles(false);
    }
  }, [canViewRoleManagement]);

  const loadPermissions = useCallback(async () => {
    if (!canViewRoleManagement) {
      setPermissions([]);
      return;
    }
    try {
      const data = await apiService.getPermissions();
      setPermissions(data);
    } catch (error) {
      console.error('Failed to load permissions:', error);
      setStatusMessage({ type: 'error', text: 'Unable to load permissions' });
    }
  }, [canViewRoleManagement]);

  useEffect(() => {
    if (canViewRoleManagement) {
      void Promise.all([loadRoles(), loadPermissions()]);
    }
  }, [canViewRoleManagement, loadPermissions, loadRoles]);

  const resetForm = () => {
    setSelectedRoleId(null);
    setRoleName('');
    setRolePermissions([]);
  };

  const handleRoleSelect = (role: RoleDetails) => {
    setSelectedRoleId(role.id);
    setRoleName(role.name);
    setRolePermissions(role.permissions.map((permission) => permission.name));
    setStatusMessage(null);
  };

  const handleRoleSave = async () => {
    if (!canManageRoleManagement) {
      setStatusMessage({ type: 'error', text: 'Insufficient permissions to manage roles' });
      return;
    }
    if (!roleName.trim()) {
      setStatusMessage({ type: 'error', text: 'Role name is required' });
      return;
    }

    setRoleSaving(true);
    try {
      if (selectedRoleId) {
        await apiService.updateRole(selectedRoleId, roleName.trim(), rolePermissions);
        setStatusMessage({ type: 'success', text: 'Role updated successfully' });
      } else {
        await apiService.createRole(roleName.trim(), rolePermissions);
        setStatusMessage({ type: 'success', text: 'Role created successfully' });
      }
      await loadRoles();
    } catch (error: any) {
      console.error('Failed to save role:', error);
      setStatusMessage({
        type: 'error',
        text: error?.response?.data?.message || 'Failed to save role',
      });
    } finally {
      setRoleSaving(false);
    }
  };

  const handleRoleDelete = async () => {
    if (!canManageRoleManagement) {
      setStatusMessage({ type: 'error', text: 'Insufficient permissions to manage roles' });
      return;
    }
    if (!selectedRoleId) return;

    setRoleSaving(true);
    try {
      await apiService.deleteRole(selectedRoleId);
      setStatusMessage({ type: 'success', text: 'Role deleted successfully' });
      resetForm();
      await loadRoles();
    } catch (error: any) {
      console.error('Failed to delete role:', error);
      setStatusMessage({
        type: 'error',
        text: error?.response?.data?.message || 'Failed to delete role',
      });
    } finally {
      setRoleSaving(false);
    }
  };

  const handlePermissionChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setRolePermissions(typeof value === 'string' ? value.split(',') : value);
  };

  const handleClearStatus = () => setStatusMessage(null);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" gutterBottom>
        Role & Permission Management
      </Typography>
      {statusMessage && (
        <Alert severity={statusMessage.type} onClose={handleClearStatus} sx={{ mb: 2 }}>
          {statusMessage.text}
        </Alert>
      )}
      {canViewRoleManagement ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '280px 1fr' },
            gap: 2,
            mt: 2,
          }}
        >
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Role Directory
              </Typography>
              {loadingRoles ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <List dense>
                  {roles.map((role) => (
                    <ListItem key={role.id} disablePadding>
                      <ListItemButton
                        selected={selectedRoleId === role.id}
                        onClick={() => handleRoleSelect(role)}
                      >
                        <ListItemIcon>
                          <Security />
                        </ListItemIcon>
                        <ListItemText
                          primary={role.name}
                          secondary={`${role.permissions.length} permissions`}
                        />
                      </ListItemButton>
                      <IconButton edge="end" onClick={() => handleRoleSelect(role)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </ListItem>
                  ))}
                  {roles.length === 0 && (
                    <ListItem>
                      <ListItemText primary="No roles available yet." />
                    </ListItem>
                  )}
                </List>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {selectedRoleId ? 'Edit Role' : 'Create Role'}
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Role Name"
                  value={roleName}
                  onChange={(event) => setRoleName(event.target.value)}
                  fullWidth
                />
                <FormControl fullWidth>
                  <InputLabel id="role-permissions-label">Permissions</InputLabel>
                  <Select
                    labelId="role-permissions-label"
                    multiple
                    value={rolePermissions}
                    onChange={handlePermissionChange}
                    label="Permissions"
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {permissions.map((permission) => (
                      <MenuItem key={permission.name} value={permission.name}>
                        <Checkbox checked={rolePermissions.indexOf(permission.name) > -1} />
                        <ListItemText primary={permission.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={handleRoleSave}
                    disabled={roleSaving || !canManageRoleManagement}
                  >
                    {selectedRoleId ? 'Update Role' : 'Create Role'}
                  </Button>
                  <Button variant="outlined" onClick={resetForm} disabled={roleSaving}>
                    Reset
                  </Button>
                  {selectedRoleId && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleRoleDelete}
                      disabled={roleSaving || !canManageRoleManagement}
                    >
                      Delete Role
                    </Button>
                  )}
                </Stack>
                <Divider />
                <Typography variant="body2" color="text.secondary">
                  Select a role from the directory to edit it. Use the permissions dropdown to tune
                  what the role can access.
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      ) : (
        <Typography color="text.secondary">You need admin role management access to view this page.</Typography>
      )}
    </Box>
  );
};

export default RoleManagementPage;
